import { useTranslation } from 'react-i18next';
import {
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	BarChart,
	Bar
} from 'recharts';
import { Badge, Button, Card, NoData } from 'src/containers/Elements';

import { Colors } from 'src/types/Color';
import { useForm } from 'react-hook-form';
import { DatePickerController } from 'src/components/elements/control/DatePickerController';
import { FormGroup, Label, Row, Col } from 'reactstrap';
import { useEffect, useMemo } from 'react';
import { useCPProfileInsight } from 'src/hooks/useCommunityPartner';
import SkeletonCPProfileStatistics from '../skeleton/SkeletonCPProfileStatistics';

const CPProfileStatistics = () => {
	const { t } = useTranslation();
	// Date range form for last 90 days
	const ninetyDaysAgo = new Date();
	ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
	const today = new Date();

	const {
		control,
		getValues,
		formState: { errors }
	} = useForm({
		defaultValues: {
			fromDate: ninetyDaysAgo,
			toDate: today
		}
	});

	const { mutate, data, isLoading } = useCPProfileInsight();

	useEffect(() => {
		mutate({
			fromDate: ninetyDaysAgo.toISOString(),
			toDate: today.toISOString()
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const submitHandler = () => {
		mutate({
			fromDate: getValues('fromDate').toISOString(),
			toDate: getValues('toDate').toISOString()
		});
	};

	// Get all unique sources
	const sources = useMemo(() => {
		if (!data) return [];
		return Array.from(
			new Set(data.map((item: any) => String(item.sourceView)))
		);
	}, [data]);

	// Group data by date and source
	const groupedData = useMemo(() => {
		if (!data) return [];
		const byDate: Record<string, any> = {};
		data.forEach((item: any) => {
			const dateKey = new Date(item.date).toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric'
			});
			if (!byDate[dateKey]) byDate[dateKey] = { date: dateKey };
			byDate[dateKey][`${item.sourceView}_view`] = item.partnerViewCount;
		});
		return Object.values(byDate);
	}, [data]);

	const barColors = [
		'#3b82f6', // blue
		'#10b981', // green
		'#8b5cf6', // purple
		'#f59e42', // orange
		'#ef4444', // red
		'#facc15', // yellow
		'#6366f1' // indigo
	];

	const getColorForSource = (source: string, idx: number) =>
		barColors[idx % barColors.length];

	return (
		<div>
			<form>
				<Row className="g-2 align-items-center ">
					<Col xs={12} md={3} lg={2}>
						<FormGroup className="mb-0">
							<Label htmlFor="fromDate">{t('forms.from')}</Label>
							<DatePickerController
								name="fromDate"
								control={control}
								error={errors.fromDate}
								maxDate={today}
								placeholder={t('forms.select-date')}
							/>
						</FormGroup>
					</Col>
					<Col xs={12} md={3} lg={2}>
						<FormGroup className="mb-0">
							<Label htmlFor="toDate">{t('forms.to')}</Label>
							<DatePickerController
								name="toDate"
								control={control}
								error={errors.toDate}
								maxDate={today}
								placeholder={t('forms.select-date')}
							/>
						</FormGroup>
					</Col>
					<Col
						xs={12}
						md={2}
						lg={1}
						className="d-flex align-items-end">
						<Button
							type="button"
							onClick={submitHandler}
							color={Colors.primary}
							className="w-100 mt-2">
							{t('button.submit')}
						</Button>
					</Col>
				</Row>
			</form>
			<Card className="d-flex flex-column gap-3 py-4">
				{isLoading && <SkeletonCPProfileStatistics />}
				{data?.length > 0 && (
					<Row className="g-2">
						<Col xs={12}>
							<Badge
								color={Colors['green']}
								className="d-flex align-items-center gap-2 p-2 fs-5 w-100 rounded-5 justify-content-between">
								<div className="d-flex gap-2 align-items-center">
									<span className="fs-2">
										{data?.reduce(
											(sum: number, item: any) =>
												sum +
												(item?.partnerViewCount || 0),
											0
										)}
									</span>
									<div className="d-flex flex-column">
										<span style={{ fontSize: '14px' }}>
											{t('label.views')}
										</span>
									</div>
								</div>
							</Badge>
						</Col>
					</Row>
				)}
				{!data?.length && !isLoading ? (
					<NoData />
				) : (
					<>
						{data?.length > 0 && (
							<div style={{ marginLeft: 0 }}>
								<ResponsiveContainer width="100%" height={340}>
									<BarChart
										data={groupedData}
										barCategoryGap={
											groupedData.length <= 2
												? '40%'
												: '10%'
										}
										barGap={
											groupedData?.length <= 2 ? 20 : 4
										}
										margin={{
											top: 20,
											right: 30,
											left: 0,
											bottom: 20
										}}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis
											dataKey="date"
											tick={{
												fontWeight: 600,
												fontSize: 14
											}}
										/>
										<YAxis
											tick={{
												fontWeight: 600,
												fontSize: 14
											}}
										/>
										<Tooltip
											cursor={{
												fill: '#e6f4ea',
												opacity: 0.25
											}}
											contentStyle={{
												backgroundColor: '#f6fff8',
												borderColor: '#b1d8b7',
												borderRadius: 8,
												boxShadow: '0 2px 8px #b1d8b733'
											}}
											itemStyle={{
												color: '#3a5a40',
												fontWeight: 500
											}}
											labelStyle={{
												color: '#588157',
												fontWeight: 700
											}}
											formatter={(value, name) => {
												if (
													typeof name === 'string' &&
													name.endsWith('_view')
												)
													return [
														`${value}`,
														`${t(
															'label.views'
														)} (${name.replace(
															'_view',
															''
														)})`
													];
												return [value, String(name)];
											}}
										/>
										<Legend
											wrapperStyle={{
												fontWeight: 500,
												fontSize: 14
											}}
										/>
										{sources.map((source, idx) => (
											<Bar
												key={`${source}_view`}
												dataKey={`${source}_view`}
												fill={getColorForSource(
													String(source),
													idx
												)}
												name={`${t(
													'label.views'
												)} (${String(source)})`}
												barSize={50}
											/>
										))}
									</BarChart>
								</ResponsiveContainer>
							</div>
						)}
					</>
				)}
			</Card>
		</div>
	);
};

export default CPProfileStatistics;
